import { supabase } from './client';
import type { NoteLocation } from '@/types/location';

export interface StoreAddress {
  id: string;
  store_id: string;
  label: string;
  location: NoteLocation;
  is_primary: boolean;
  floor?: string;
  apartment?: string;
  notes?: string;
  created_at?: string;
}

export async function getStoreAddresses(storeId: string): Promise<StoreAddress[]> {
  const { data, error } = await supabase
    .from('store_addresses')
    .select('*')
    .eq('store_id', storeId)
    .order('is_primary', { ascending: false });
  if (error) throw error;
  if (!data) return [];
  return data.map((row: any) => ({
    id: row.id,
    store_id: row.store_id,
    label: row.label,
    is_primary: row.is_primary,
    location: {
      displayName: row.location_display_name || row.label || '',
      latitude: row.latitude,
      longitude: row.longitude,
    },
    floor: row.floor ?? '',
    apartment: row.apartment ?? '',
    notes: row.notes ?? '',
    created_at: row.created_at,
  }));
}

export async function upsertStoreAddress(address: Omit<StoreAddress, 'id'> & { id?: string }): Promise<StoreAddress> {
  const { id, store_id, label, is_primary, location, floor, apartment, notes } = address;
  const row: any = {
    store_id,
    label,
    is_primary,
    location_display_name: location.displayName,
    latitude: location.latitude,
    longitude: location.longitude,
    floor,
    apartment,
    notes,
  };
  if (id) row.id = id;
  const { data, error } = await supabase
    .from('store_addresses')
    .upsert([row], { onConflict: 'id' })
    .select()
    .single();
  if (error) throw error;
  return {
    id: data.id,
    store_id: data.store_id,
    label: data.label,
    is_primary: data.is_primary,
    location: {
      displayName: data.location_display_name,
      latitude: data.latitude,
      longitude: data.longitude,
    },
    floor: data.floor,
    apartment: data.apartment,
    notes: data.notes,
    created_at: data.created_at,
  };
}

export async function deleteStoreAddress(id: string): Promise<void> {
  const { error } = await supabase
    .from('store_addresses')
    .delete()
    .eq('id', id);
  if (error) throw error;
}
